import { BigSwitch, BoxContainer, Gauge, PrimaryButton, ReactIcons, StarRating } from "@kalyzee/kalyzee-ui";

import workBoxStyles from "../WorkBox.module.css";

import surveyWorkBoxStyles from "./SurveyWorkBox.module.css";

import { isMeetingSessionRoleGreaterThanOrEqual, MeetingContainerAPI, useMeetingSelector } from "@kalyzee/kast-web-sdk";
import { Loading } from "@kalyzee/kast-web-sdk/web-components";
import { MeetingSessionRole, ResultEmittedMessage, Survey, SurveyStatus } from "@kalyzee/kast-web-sdk/websocket";
import clsx from "clsx";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useSdkContext } from "../../../contexts/sdk/context";
import { Card } from "../../common/Card";
import { Grid } from "../../common/Grid";
import { SurveyResultsViewer } from "../../survey/SurveyResultsViewer";
import { MainClockElapsedRenderer } from "../../renderers/mainClock";
import { set } from "lodash";
import { SurveyAnswerCreateQuestion } from "@kalyzee/kast-websocket-module/dist/esm/commands/survey/interfaces";
import { toastError, toastSuccess } from "../../../helpers/toast";

export const SurveyViewerWorkBox = ({
  meetingAPI,
  survey,
  isAdmin,
  onClose,
}: {
  meetingAPI: MeetingContainerAPI;
  survey?: Survey;
  isAdmin?: boolean;
  onClose?: () => void;
}) => {
  const [sessions] = useMeetingSelector(meetingAPI, "sessions");

  const questionData = useMemo(() => {
    const question = survey?.questions[0];
    if (!question) return null;
    return {
      question,
      survey,
      minChoices: question.minChoiceNumber || 1,
      maxChoices: question.maxChoiceNumber || 1,
      selfAnswer: survey.selfAnswers?.[0], // Only one answer enabled for now
    }
  }, [survey]);

  const getDraft = () => ({
    content: new Set(questionData?.selfAnswer?.questions?.[0].choiceIds),
    updated: false,
  });

  const [answerDraft, setAnswerDraft] = useState<{ content: Set<string>, updated: boolean }>(getDraft());

  // Active view for an expert (admin) on an open survey: results management or their own answer.
  const [expertView, setExpertView] = useState<"results" | "answer">("results");

  // Once the user has submitted an answer, their answer is shown read-only until they
  // explicitly choose to edit it.
  const [isEditing, setIsEditing] = useState(false);

  const hasAnswered = !!questionData?.selfAnswer;
  const readOnly = hasAnswered && !isEditing;

  useEffect(() => {
    setAnswerDraft(getDraft());
    setIsEditing(false);
  }, [questionData?.selfAnswer]);

  const onlineUserIds = useMemo(() => {
    const ids = new Set<string>();
    sessions?.forEach((session) => {
      const id = session.user?.id;
      if (id) ids.add(id);
    });
    return ids;
  }, [sessions]);

  const surveyStats = useMemo(() => {
    const userIds = new Set<string>();
    survey?.answers?.forEach((answer) => {
      userIds.add(answer.userId);
    });
    const onlineAnswers: Set<string> = new Set();
    const offlineAnswers: Set<string> = new Set();
    userIds.forEach((id) => {
      if (onlineUserIds.has(id)) onlineAnswers.add(id);
      else offlineAnswers.add(id);
    });
    return { onlineAnswers: onlineAnswers, offlineAnswers: offlineAnswers };
  }, [survey, onlineUserIds]);

  const surveyResultViewerCustomColors = useMemo(() => ({
    normal: [{
      background: "var(--color-element-workbox-survey-viewer-bg)",
      fill: "var(--color-element-workbox-survey-viewer-fill)",
      text: "var(--color-white)",
      subText: "var(--color-element-workbox-survey-text-2)",
      starFull: "var(--color-element-workbox-survey-star-fill)",
      starEmpty: "var(--color-element-workbox-survey-star-bg)",
    }],
    averageStars: {
      empty: "rgba(var(--color-purple-1-rgb), 0.25)",
      full: "var(--color-element-workbox-survey-star-fill)",
      text: "var(--color-element-workbox-survey-star-fill)"
    }
  }), []);

  const sdkCtx = useSdkContext();

  // useEffect(() => {
  //   if (questionData?.selfAnswered) {
  //     // My answers
  //     setSelectedAnswersDraft(undefined);
  //   }
  // }, [questionData?.selfAnswered]);

  const onPublishResults = useCallback(async () => {
    const client = sdkCtx.client.getWebsocketClient();
    if (!client || !survey) return;
    const result = await client.emitter.survey.close({
      surveyId: survey.id,
    });
    if (result?.error) {
      console.error("Error publishing survey results: ", result.error);
      toastError("Impossible de publier les résultats");
      return;
    }
    toastSuccess("Résultats publiés");
  }, [sdkCtx, survey]);

  const onDeleteSurvey = useCallback(async () => {
    const client = sdkCtx.client.getWebsocketClient();
    if (!client || !survey) return;
    const result = await client.emitter.survey.remove({
      surveyId: survey.id,
    });
    if (result?.error) {
      console.error("Error ending survey: ", result.error);
      toastError("Impossible de finir le sondage")
      return;
    }
    toastSuccess("Fin du sondage");
  }, [sdkCtx, survey]);

  const onAnswerSurvey = useCallback(async () => {
    const client = sdkCtx.client.getWebsocketClient();

    if (!client || !questionData) return;
    // Editing an existing answer without changing anything: just leave edit mode.
    if (!answerDraft?.updated) {
      setIsEditing(false);
      return;
    }
    if (answerDraft.content.size < questionData.minChoices || answerDraft.content.size > questionData.maxChoices) return;

    const questions: SurveyAnswerCreateQuestion[] = [{
      id: questionData.question.id,
      choiceIds: Array.from(answerDraft.content),
    }];

    let result: ResultEmittedMessage;

    const isUpdate = !!questionData.selfAnswer;
    if (questionData.selfAnswer) {
      result = await client.emitter.survey.answerUpdate({
        id: questionData.selfAnswer.id,
        questions,
      });
    } else {
      result = await client.emitter.survey.answerCreate({
        surveyId: questionData.survey.id,
        questions,
      });
    }

    if (result?.error || !result.response) {
      console.error("Error answering survey: ", result.error);
      return;
    }

    // setAnswerDraft(undefined);
    setIsEditing(false);
    meetingAPI.store.dispatch("surveyAnswerCreate", { answer: result.response, isSelf: true });
    toastSuccess(isUpdate ? "Réponse modifiée" : "Réponse publiée");
  }, [sdkCtx.client, questionData, answerDraft, meetingAPI.store]);

  const onSelectChoice = useCallback((choiceId: string) => {
    const question = survey?.questions[0];
    if (!question) return;

    const maxChoice = question.maxChoiceNumber || 1;

    if (answerDraft.content.size >= maxChoice && maxChoice > 1 && !answerDraft.content.has(choiceId)) {
      // we don't know which choice to clear, so we do nothing
      return;
    }

    const newSelected = new Set(answerDraft.content);
    if (newSelected.has(choiceId)) {
      newSelected.delete(choiceId);
    } else {
      if (maxChoice === 1 && newSelected.size === 1) {
        // single choice, clear previous choice
        newSelected.clear();
      }
      newSelected.add(choiceId);
    }

    setAnswerDraft({
      content: newSelected,
      updated: true,
    });
  }, [answerDraft, survey?.questions]);

  const renderChoicesString = (min: number, max: number, total: number) => {
    if (questionData?.selfAnswer) {
      return "Vous avez soumis votre réponse.";
    }
    const minPlural = min > 1 ? 's' : '';
    if (min === max) {
      return `Choisissez ${min} option${minPlural}`;
    }
    if (max === total) {
      return `Choisissez au moins ${min} option${minPlural}`;
    }
    return `Choisissez entre ${min} et ${max} options`;
  }

  const renderChoiceOptions = () => {
    if (!questionData) return;

    if (questionData.survey.name === "Étoiles") {
      const choice = questionData.question.choices.find(
        choice => answerDraft.content.has(choice.id)
      );
      let numericValue;
      try {
        numericValue = choice ? parseFloat(choice.content) : undefined;
      } catch (error) {
        console.error("Error parsing choice content to numeric value:", error);
        numericValue = undefined;
      }
      return (
        <div className={surveyWorkBoxStyles.starsWrapper}>
          <StarRating
            maxStars={5}
            minStars={0.5}
            allowHalfStars
            showHoverPreview={!readOnly}
            value={numericValue}
            onChange={readOnly ? undefined : (choice) => {
              const selectedId = questionData.question.choices.find(c => c.content === choice.toString())?.id;
              if (selectedId) {
                onSelectChoice(selectedId);
              }
            }}
            starColors={{
              empty: "rgba(var(--color-purple-1-rgb), 0.25)",
              full: "var(--color-element-workbox-survey-star-fill)",
            }}
            starStyle={{
              width: "5em",
              height: "5em",
            }}
            containerClassName={clsx({
              [surveyWorkBoxStyles.readOnlyAnswer]: readOnly,
            })}
          />
        </div>
      )
    }
    return (
      <div
        className={clsx(surveyWorkBoxStyles.choicesGrid, {
          [surveyWorkBoxStyles.readOnlyAnswer]: readOnly,
        })}
      >
        <Grid
          elements={
            questionData.question.choices.map((choice) => {
              return (
                <Card.Radio
                  key={`choice-${choice.id}`}
                  text={choice.content}
                  checked={answerDraft.content.has(choice.id)}
                  onClick={readOnly ? undefined : () => onSelectChoice(choice.id)}
                  // disabled={selfAnswered || (questionData.maxChoices > 1 && selectedAnswerIds.size >= questionData.maxChoices && !selectedAnswerIds.has(choice.id))}
                />
              );
            })
          }
        />
      </div>
    );
  };

  const renderAnswerOptions = () => {
    if (!questionData) {
      return (
        <div>Le sondage ne contient pas de question.</div>
      );
    }
    if (questionData.question.choices.length === 0) {
      return (
        <div>Le sondage ne contient pas de choix.</div>
      );
    }
    const header = (
      <div className={surveyWorkBoxStyles.contentTitleBlock}>
        <div className={surveyWorkBoxStyles.contentTitle}>{questionData.question.content}</div>
        <div className={clsx(surveyWorkBoxStyles.textColor2, surveyWorkBoxStyles.contentSubTitle)}>
          {renderChoicesString(
            questionData.minChoices,
            questionData.maxChoices,
            questionData.question.choices.length,
          )}
        </div>
      </div>
    );
    return (
      <>
        {header}
        {renderChoiceOptions()}
        {readOnly ? (
          <button
            type="button"
            className={surveyWorkBoxStyles.editLink}
            onClick={() => setIsEditing(true)}
          >
            Modifier ma réponse
          </button>
        ) : (
          <PrimaryButton
            className={clsx({
              [surveyWorkBoxStyles.validateButton]: true,
              // When editing an existing answer, keep the button at full color (the
              // previous selection is already valid); only the first answer stays
              // disabled until a valid choice is made.
              [surveyWorkBoxStyles.disabledBtn]: answerDraft.content.size < questionData.minChoices || (!hasAnswered && !answerDraft.updated),
            })}
            onClick={onAnswerSurvey}
          >
            {hasAnswered ? (
              <div style={{ display: "flex", alignItems: "center", gap: "1em" }}>
                Valider la modification
                <ReactIcons.Edit style={{ height: "1.5em" }} />
              </div>
            ) : "Valider ma réponse"}
          </PrimaryButton>
        )}
      </>
    );
  };

  const renderAnswerGauge = () => {
    return (
      <div className={surveyWorkBoxStyles.viewerStatsAnswers}>
        <div>
          <span className={surveyWorkBoxStyles.textColor1}>
            {surveyStats.onlineAnswers.size} réponse{surveyStats.onlineAnswers.size > 1 ? "s" : ""}
          </span>
          <span className={surveyWorkBoxStyles.textColor2}>
            {` / ${onlineUserIds.size} participant${onlineUserIds.size > 1 ? "s" : ""} connecté${onlineUserIds.size > 1 ? "s" : ""}`}
          </span>
        </div>
        <Gauge
          direction="horizontal"
          fillValue={surveyStats.onlineAnswers.size}
          fillCapacity={onlineUserIds.size}
          classNameBg={surveyWorkBoxStyles.gaugeBg}
          classNameFill={surveyWorkBoxStyles.gaugeFill}
        />
        <div className={surveyWorkBoxStyles.textColor2}>
          {surveyStats.offlineAnswers.size > 0 ? `(+ ${surveyStats.offlineAnswers.size} participant${surveyStats.offlineAnswers.size > 1 ? "s" : ""} déconnecté${surveyStats.offlineAnswers.size > 1 ? "s" : ""})` : ""}
        </div>
      </div>
    );
  };

  const renderTimer = () => {
    if (!survey) return null;
    if (survey.status === SurveyStatus.Closed) return null;
    return (
      <MainClockElapsedRenderer
        since={survey.createdAt}
        render={elapsed => (
          <div className={surveyWorkBoxStyles.timer}>
            <ReactIcons.Timer />
            {elapsed}
          </div>
        )}
      />
    );
  };

  const renderCloseBtn = () => {
    if (!isAdmin) return null;
    const status = survey?.status;
    if (!status) return null;
    return (
      <PrimaryButton
        className={clsx({ [surveyWorkBoxStyles.validateButton]: true, [surveyWorkBoxStyles.closed]: status === SurveyStatus.Closed })}
        text={status === SurveyStatus.Open ? "Publier les résultats" : "Mettre fin au sondage"}
        onClick={status === SurveyStatus.Open ? onPublishResults : onDeleteSurvey}
      />
    );
  };

  const renderSurveyResults = () => {
    if (!questionData) return null;
    return (
      <>
        <div style={{
          flex: 1,
          minHeight: 0,
          alignItems: "center",
          width: "100%",
          display: "flex",
          flexDirection: "column",
        }}>
          <div className={surveyWorkBoxStyles.contentTitleBlock}>
            <div className={surveyWorkBoxStyles.titleWithBadge}>
              <div className={surveyWorkBoxStyles.contentTitle}>{questionData.question.content}</div>
              {survey?.status === SurveyStatus.Closed && (
                <div className={surveyWorkBoxStyles.publishedBadge}>Résultats publiés</div>
              )}
            </div>
          </div>
          {renderAnswerGauge()}
          <div style={{
            flex: 1,
            width: "100%",
            minHeight: 0,
          }}>
            <SurveyResultsViewer
              survey={survey}
              customColors={surveyResultViewerCustomColors}
            />
          </div>
        </div>
        {renderCloseBtn()}
      </>
    );
  }

  const renderExpertSwitch = () => (
    <BigSwitch
      className={surveyWorkBoxStyles.expertSwitch}
      options={[
        { value: "results" as const, label: "Résultats" },
        { value: "answer" as const, label: "Votre réponse" },
      ]}
      value={expertView}
      onChange={setExpertView}
    />
  );

  const renderContent = () => {
    if (!survey) {
      return (
        <div style={{ width: "100%", height: "100%", display: "flex", justifyContent: "center", alignItems: "center" }}>Pas de sondage en cours.</div>
      );
    }
    const isOpen = survey.status !== SurveyStatus.Closed;
    if (!isAdmin) {
      return isOpen ? renderAnswerOptions() : renderSurveyResults();
    }
    // Expert (admin): on a closed survey, only the results are relevant.
    if (!isOpen) {
      return renderSurveyResults();
    }
    // Open survey: the expert can switch between results management and their own answer.
    return (
      <>
        {renderExpertSwitch()}
        {expertView === "answer" ? renderAnswerOptions() : renderSurveyResults()}
      </>
    );
  };

  return (
    <BoxContainer
      className={surveyWorkBoxStyles.container}
      topBarProps={{
        left: [
          <ReactIcons.Stats style={{ height: "1.5em", width: "1.5em" }} />,
        ],
        center: [
          <span className={workBoxStyles.label}>Sondage</span>
        ],
        right: onClose ? [
          <PrimaryButton onClick={onClose}>
            <ReactIcons.Close style={{ height: "0.8em" }} />
          </PrimaryButton>
        ] : [],
      }}
    >
      <div className={surveyWorkBoxStyles.content}>
        {renderContent()}
        {renderTimer()}
      </div>
    </BoxContainer>
  );
}
